import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createContext, useContext } from 'react';
import { api, type HealthResponse } from '../services/api';
import type { PolicySystemState } from '../content/policySystemMessages';

const POLL_INTERVAL_MS = 60_000;

type PolicySystemStatusContextValue = PolicySystemState & {
  loading: boolean;
  refresh: () => Promise<void>;
  showBanner: boolean;
  isUnavailable: boolean;
  isDegraded: boolean;
};

const defaultState: PolicySystemStatusContextValue = {
  status: 'ok',
  loading: true,
  refresh: async () => {},
  showBanner: false,
  isUnavailable: false,
  isDegraded: false,
};

const PolicySystemStatusContext = createContext<PolicySystemStatusContextValue>(defaultState);

function snapshotFromHealth(data: HealthResponse): PolicySystemState {
  const ps = data.policySystem;
  return {
    status: ps?.status ?? 'ok',
    incidentRef: ps?.incidentRef,
    retryAfterSec: ps?.retryAfterSec,
    reason: ps?.reason,
    since: ps?.since,
  };
}

type PolicySystemStatusProviderProps = {
  children: ReactNode;
  /** Disable polling (e.g. dev mock mode). */
  enabled?: boolean;
};

/**
 * Polls GET /api/health for policy-system availability and exposes banner/gate state.
 */
export function PolicySystemStatusProvider({
  children,
  enabled = true,
}: PolicySystemStatusProviderProps) {
  const [state, setState] = useState<PolicySystemState>({ status: 'ok' });
  const [loading, setLoading] = useState(enabled);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    try {
      const data = await api.getHealth();
      setState(snapshotFromHealth(data));
    } catch {
      setState({ status: 'unavailable' });
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void refresh();
    if (!enabled) {
      return;
    }
    const id = window.setInterval(() => {
      void refresh();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [enabled, refresh]);

  const value = useMemo<PolicySystemStatusContextValue>(() => {
    const status = state.status;
    return {
      ...state,
      loading,
      refresh,
      showBanner: status === 'degraded' || status === 'unavailable',
      isUnavailable: status === 'unavailable',
      isDegraded: status === 'degraded',
    };
  }, [state, loading, refresh]);

  return (
    <PolicySystemStatusContext.Provider value={value}>
      {children}
    </PolicySystemStatusContext.Provider>
  );
}

export function usePolicySystemStatus(): PolicySystemStatusContextValue {
  return useContext(PolicySystemStatusContext);
}
